import { colors } from '~/styles/colors';
import { HealthCountInfo, HealthUnitsInfo } from '../contexts/HealthDataContext';
import { HealthVariant } from '../Health/components/UnitsInfo/types';
import { GetOverviewUnitHealth } from '../types';
import { t } from 'i18next';

export function parseHealthData(data: GetOverviewUnitHealth): HealthCountInfo {
  return {
    alert: data.COUNT_TOTAL_ALERT_UNITS,
    critical: data.COUNT_TOTAL_CRITICAL_UNITS,
    healthy: data.COUNT_TOTAL_HEALTHY_UNITS,
    inoperative: data.COUNT_TOTAL_INOPERANT_UNITS,
  };
}

export function parseUnits(
  data: GetOverviewUnitHealth['UNITS'],
  offlineRuleActive = false,
): HealthUnitsInfo {
  return data.map((item) => ({
    id: item.UNIT_ID,
    lat: item.UNIT_LAT ? Number(item.UNIT_LAT) : null,
    lon: item.UNIT_LON ? Number(item.UNIT_LON) : null,
    name: item.UNIT_NAME,
    state: item.STATE_NAME,
    city: item.CITY_NAME,
    cityId: item.CITY_ID,
    health: parseUnitHealth(item.UNIT_INDEX, { isOffline: item.IS_OFFLINE === true, offlineRuleActive }),
    machines: {
      alert: item.COUNT_ALERT_MACHINES,
      critical: item.COUNT_CRITICAL_MACHINES,
      healthy: item.COUNT_HEALTHY_MACHINES,
      inoperative: item.COUNT_INOPERANT_MACHINES,
    },
  }));
}

/**
 * A unidade offline (todos os dispositivos sem comunicação, informado pela API) tem precedência sobre o índice.
 * Com a regra de offline por conectividade ligada, índice 0 deixa de significar offline: a unidade sem ativos
 * com saúde fica fora dos cards. Com a regra desligada, mantém a classificação apenas pelo índice.
 */
export function parseUnitHealth(
  unitIndex: number,
  { isOffline, offlineRuleActive }: { isOffline: boolean; offlineRuleActive: boolean },
): HealthVariant {
  if (isOffline) return 'inoperative';
  if (offlineRuleActive && unitIndex === 0) return 'exception';
  if (unitIndex >= 0.85) return 'healthy';
  if (unitIndex >= 0.45 && unitIndex < 0.85) return 'alert';
  if (unitIndex > 0 && unitIndex < 0.45) return 'critical';
  if (unitIndex === -1) return 'exception';
  return 'inoperative';
}

/** Sem o campo (API antiga), a regra de offline por conectividade é considerada desligada. */
export function isOfflineRuleActive(data: Pick<GetOverviewUnitHealth, 'OFFLINE_BY_CONNECTIVITY_ENABLED'>): boolean {
  return data.OFFLINE_BY_CONNECTIVITY_ENABLED === true;
}

/**
 * Com a regra de offline ativa, a base do percentual inclui as unidades offline, igual ao histórico da API.
 * Sem ela, mantém a base antiga (saudáveis + alerta + crítico).
 */
export function computeHealthyPercentage(data: HealthCountInfo, offlineRuleActive: boolean): number {
  const total = data.alert + data.critical + data.healthy + (offlineRuleActive ? data.inoperative : 0);

  if (Number(total) === 0) return 0;

  return Math.floor(data.healthy * 100 / total);
}

export function parseHealthColor(health: HealthVariant): string {
  switch (health) {
    case 'alert': return colors.state.warning.base;
    case 'critical': return colors.state.danger.base;
    case 'healthy': return colors.state.success.base;
    default: return colors.icon['soft-400'];
  }
}

enum HealthStatus {
  OFFLINE = 2,
  DISABLED = 4,
  URGENT_MAINTENANCE = 25,
  IMMINENT_RISK = 50,
  OUT_OF_SPECIFICATION = 75,
  OPERATING_CORRECTLY = 100,
}

export const healthCodes = {
  [HealthStatus.OFFLINE]: t('equipamentoOffline'),
  [HealthStatus.DISABLED]: t('maquinaDesativada'),
  [HealthStatus.URGENT_MAINTENANCE]: t('manutencaoUrgente'),
  [HealthStatus.IMMINENT_RISK]: t('riscoIminente'),
  [HealthStatus.OUT_OF_SPECIFICATION]: t('foraDeEspecificacao'),
  [HealthStatus.OPERATING_CORRECTLY]: t('operandoCorretamente'),
};
